import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { XMarkIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import SchematStringow from './SchematStringow'
import PasekWarunku from './PasekWarunku'
import SekcjaOplacalnosci from './SekcjaOplacalnosci'
import { DOMYSLNE_DANE, odczytajDaneProjektu } from '../../lib/daneProjektu'
import { daneProjektuZEnergii } from '../../lib/klienci/energia'
import { opisZrodlaProdukcji } from '../../lib/klienci/zrodlaEkonomii'
import { KONSTRUKCJA_DOMYSLNA } from '../../lib/konstrukcja'
import { warunkiDoboru } from '../../lib/warunkiDoboru'
import { ekonomiaKlienta, wejscieOferty } from '../../lib/ekonomiaKlienta'
import { pobierzModel } from '../../api/katalog3d'
import { magazynZKatalogu } from '../../lib/projekt3d/katalog'
import { zmienneKoloru } from '../../lib/kolorDokumentu'
import { zaletyFalownika, zaletyMagazynu, zaletyModulu } from '../../lib/zaletyKomponentow'
import { pobierzAtrybuty, pobierzAtrybutyMagazynu } from '../../api/falowniki'
import useUstawieniaWyceny from '../../hooks/useUstawieniaWyceny'
import StronaDokumentu, { NaglowekSekcji } from './StronaDokumentu'
import StronaTytulowa from './StronaTytulowa'
import StronaKomponentow from './StronaKomponentow'
import StronaProdukcji from './StronaProdukcji'
import StronaCeny from './StronaCeny'
import StronaInstalacji from './StronaInstalacji'
import DiagramEnergii from './DiagramEnergii'
import useUjecie3D from '../../hooks/useUjecie3D'
import { dataPl } from '../../lib/oferta/oferta'
import useKosztZestawu from '../../hooks/useKosztZestawu'
import { useAuth } from '../../context/AuthContext'
import { BramkaTresci, useDostepDoTresci } from '../auth/BramkaLogowania'
import OknoRejestracji from '../auth/OknoRejestracji'
import { useMozna } from '../../hooks/usePlan'
import { zdarzenie } from '../../lib/pomiar'
import { aktywnosc } from '../../lib/aktywnosc'
import { Znak } from '../oferta/ElementyRaportu'
import { KRAJ, jestPolska } from '../../kraj'

// Logo VoltValue bez żadnych przeróbek (zasada właściciela) na okładce dokumentu planu Free (FE-05).
const LOGO_VOLTVALUE = `${process.env.PUBLIC_URL || ''}/navLogo.png`

/**
 * Schemat w oknie na środku — z nagłówkiem, datą wygenerowania i zastrzeżeniem.
 *
 * DLACZEGO ZASTRZEŻENIE JEST OBOWIĄZKOWE, a nie ozdobne: wydrukowany schemat
 * z logo serwisu wygląda jak dokument projektowy. Ktoś może go pokazać
 * instalatorowi albo dołączyć do zgłoszenia do operatora. Musi być jednoznacznie
 * napisane, czym to JEST (dobór poglądowy w warunkach skrajnych), a czym NIE JEST
 * (projekt elektryczny, którego wykonanie wymaga uprawnień).
 *
 * PDF przez `window.print()`, nie przez bibliotekę: systemowy dialog druku ma
 * „Zapisz jako PDF" na każdej platformie, daje wektorowy wynik (SVG zostaje
 * SVG, nie rastrem) i nie dokłada megabajta zależności do paczki frontu.
 *
 * Plany (FE-05, decyzja właściciela 2026-10-19): PDF od konta Free. Bez konta przycisk PDF otwiera okno
 * „Załóż darmowe konto” (podgląd na ekranie zostaje). Free: logo i niebieski kolor VoltValue niezależnie od
 * zapisanych ustawień; Premium i próba (funkcja `marka_dokumentu`): logo i kolor firmy jak dotąd.
 */

/**
 * Zakładki są TYLKO na ekranie. Na wydruku wszystkie sekcje idą po kolei — bo to
 * jeden dokument, a nie trzy osobne. Podział na ekranie istnieje po to, żeby okno
 * nie startowało ścianą trzech wykresów naraz.
 */
const ZAKLADKI = [
  // „Projekt" PIERWSZY: to okładka i lista sprzętu, czyli dwie strony, które
  // klient czyta najpierw. Schemat łańcuchów jest dla instalatora i stoi dalej.
  { key: 'projekt', label: 'Projekt' },
  { key: 'schemat', label: 'Schemat' },
  { key: 'produkcja', label: 'Produkcja' },
  // Poza Polską projekt idzie BEZ opłacalności (decyzja właściciela 2026-10-08):
  // ta zakładka niesie wtedy samą stronę ceny i tak się nazywa.
  { key: 'oplacalnosc', label: 'Opłacalność', labelPozaPolska: 'cena' },
]

/** Liczby po polsku na stronie schematu (5,46 kWp, a nie 5.46). */
const pl = (v) => (v == null ? '' : String(v).replace('.', ','))

/**
 * Nazwa z katalogu bez przedrostka kategorii i samotnych myślników sklepu:
 * „Inwerter - HUAWEI - SUN 2000-12KTL-M5” → „HUAWEI SUN 2000-12KTL-M5”.
 */
const czystaNazwa = (n) => String(n || '')
  .replace(/^(moduł(\s+fotowoltaiczny)?|falownik|inwerter)\s*[-–:]?\s*/i, '')
  .replace(/\s+[-–—]\s+/g, ' ').trim()

/**
 * Jedno zdanie pod tytułem schematu. Wcześniej: „1 łańcuch × 13 modułów, razem 13 szt.
 * i 5.46 kWp na 1 z 2 wejść MPPT falownika” (liczba modułów dwa razy, kropka w liczbie).
 * Odmianę „1 moduł”, „3 moduły”, „13 modułów” niesie liczba mnoga i18next (`count`).
 */
const opisUkladu = (t, z, mpptCount) => {
  const lancuchy = z.stringow === 1
    ? t('schemat.lancuch_jeden', { count: z.modulow_razem })
    : t('schemat.lancuchy', {
      count: z.modulow_razem, stringow: z.stringow, wStringu: z.modulow_w_stringu,
    })
  const wejscia = mpptCount > 1
    ? t('schemat.wejscia', { uzyte: z.mppt_uzyte, wszystkie: mpptCount })
    : t('schemat.wejscie_jedyne')
  return t('schemat.opis_ukladu', { lancuchy, kwp: pl(z.moc_kwp), wejscia })
}

const ModalSchematu = ({
  otwarty, onZamknij, dane, zestaw = null, kompatybilnosc = null,
  formularz = null, ladowanie = false, blad = '', koszty = null, cenaZewnetrznaZl = null,
  liczbaModulowOferty = null, trybOferty = false, dotacjaOferty = null,
  // Oferta z karty klienta: dla kogo ({ nazwa, adres }), numer i ważność, cena ustalona w karcie
  // ({ netto, brutto, uslugi: nazwy usług dodatkowych w cenie }; ma pierwszeństwo przed rachunkiem) i model z Projektu 3D do ujęcia na okładce.
  klient = null, oferta = null, cenaKlienta = null, projekt3d = null,
  // Profil energetyczny klienta (`dane.energia` z adresem jako zapasem na województwo; `lib/klienci/energia.js`).
  // Gdy jest, zużycie, województwo, ulga i produkcja z Projektu 3D idą z niego, a zmiany z okna wracają do klienta
  // przez `onEdycjaEnergii(czesc)`; sessionStorage (`lib/daneProjektu.js`) zostaje tylko dla oferty bez klienta.
  energia = null, onEdycjaEnergii = null, konstrukcjaKlienta = null,
  // Suma zestawu klienta z montażem i marżą firmy (`lib/klienci/zestawKlienta.js`), gdy nie ma ceny dla klienta.
  kosztZestawuZl = null,
}) => {
  const { t } = useTranslation('projekt')
  const panelRef = useRef(null)
  // Przez wspolny hak, zeby przycisk PDF chodzil razem z bramka nad trescia.
  const { odblokowane: zalogowany } = useDostepDoTresci()
  // Tryb oferty (`/sprawdz-oferte`): klient ma już ofertę, więc bez okładki i listy sprzętu;
  // dokument zaczyna się od schematu (decyzja właściciela 2026-09-28).
  // Poza Polską zakładka „Opłacalność” niesie samą cenę, więc bez kosztów zestawu znika.
  const zakladki = ZAKLADKI.filter((k) => !(trybOferty && k.key === 'projekt')
    && !(!jestPolska && k.key === 'oplacalnosc' && !koszty))
  const [zakladka, setZakladka] = useState(trybOferty ? 'schemat' : 'projekt')
  // Oferta przy kliencie nie czyta sessionStorage w ogóle: tamte odpowiedzi należą do poprzedniej oferty w tej
  // przeglądarce i przechodziły z klienta na klienta.
  const przyKliencie = energia != null
  const [projektSesji, setProjekt] = useState(() => (energia != null ? { ...DOMYSLNE_DANE } : odczytajDaneProjektu()))
  const projekt = useMemo(
    () => (przyKliencie
      ? { ...DOMYSLNE_DANE, ...daneProjektuZEnergii(energia), konstrukcja: konstrukcjaKlienta || KONSTRUKCJA_DOMYSLNA }
      : projektSesji),
    [przyKliencie, energia, konstrukcjaKlienta, projektSesji],
  )
  // Zmiana z okna: przy kliencie do jego karty, bez klienta (jak dotąd) do odpowiedzi w sesji.
  const onEdycja = przyKliencie && onEdycjaEnergii ? onEdycjaEnergii : null
  // Zalety marek z danych badawczych. `null` znaczy „jeszcze nie wiemy" i różni
  // się od pustej listy, która znaczy „marki nie ma w badaniu" — przy pierwszym
  // renderze nie chcemy pokazać sekcji, która za chwilę się wypełni.
  const [atrybuty, setAtrybuty] = useState({ falownik: null, magazyn: null })
  // Karta katalogowa magazynu (energia użytkowa, sprawność cyklu): zwrot liczymy z niej, jak Projekt 3D.
  const [kartaMagazynu, setKartaMagazynu] = useState(null)
  const wycenaFirmy = useUstawieniaWyceny()
  const { profil: profilKonta, isAuthenticated } = useAuth()
  const { mozna: wlasnaMarka } = useMozna('marka_dokumentu')
  // Marka dokumentu: bez funkcji `marka_dokumentu` (Free) kolor platformy i logo VoltValue zamiast firmowych.
  // Reszta ustawień wyceny (marże, VAT, usługi) działa we Free bez zmian.
  const wycena = useMemo(() => {
    if (!wycenaFirmy || wlasnaMarka) return wycenaFirmy
    return { ...wycenaFirmy, document_color: null, logo_data_uri: LOGO_VOLTVALUE }
  }, [wycenaFirmy, wlasnaMarka])
  const [oknoRejestracji, setOknoRejestracji] = useState(false)
  // Ta sama kwota co na karcie zestawu — liczona wspólnym hakiem, nie drugi raz.
  const rachunek = useKosztZestawu({ ...(koszty || {}), kwp: zestaw?.kwp })
  // Cena spoza naszego rachunku: np. oferta instalatora sprawdzana w `/sprawdz-oferte`.
  // Idzie na okładkę i do zwrotu z inwestycji. Strony „Cena i zakres” wtedy nie ma,
  // bo nie wiemy, co firma wliczyła w swoją cenę.
  const cenaKoncowaZl = cenaKlienta?.brutto ?? (koszty ? rachunek.razemZl : (cenaZewnetrznaZl || null))
  const cenaNettoZl = cenaKlienta ? (cenaKlienta.netto ?? null) : (koszty ? rachunek.nettoZl : null)
  // Ujęcie z Projektu 3D liczy się w tle po otwarciu okna (kilkanaście sekund), tylko gdy klient ma model.
  const ujecie = useUjecie3D(otwarty ? projekt3d : null)

  // Dotacja z raportu oferty (szacunek PME2) podstawiana przy każdym otwarciu. Nie zapisujemy
  // jej do danych projektu: to liczba tej oferty, a nie ustawienie użytkownika.
  useEffect(() => {
    if (jestPolska && otwarty && trybOferty && dotacjaOferty != null) setProjekt((p) => ({ ...p, dotacjaZl: dotacjaOferty }))
  }, [otwarty, trybOferty, dotacjaOferty])

  useEffect(() => {
    if (!otwarty) return undefined
    const naEscape = (e) => { if (e.key === 'Escape') onZamknij() }
    document.addEventListener('keydown', naEscape)
    const poprzedni = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    panelRef.current?.focus()
    return () => {
      document.removeEventListener('keydown', naEscape)
      document.body.style.overflow = poprzedni
    }
  }, [otwarty, onZamknij])

  /**
   * Zalety marek z danych badawczych — pobierane dopiero po otwarciu okna.
   *
   * Nie przy renderze karty zestawu: kart bywa na ekranie kilkanaście, a te dane
   * są potrzebne wyłącznie w dokumencie. Dwa osobne endpointy, bo falowniki mają
   * poziom marki i serii, a magazyny wyłącznie marki.
   */
  const idFalownika = zestaw?.items?.find((p) => p.category === 'inverter')?.model_id
  const idMagazynu = zestaw?.items?.find((p) => p.category === 'battery')?.model_id

  useEffect(() => {
    if (!otwarty) return undefined
    let anulowane = false

    const wczytaj = async () => {
      const [f, m, karta] = await Promise.all([
        idFalownika ? pobierzAtrybuty([idFalownika]) : Promise.resolve({ ok: true, data: {} }),
        idMagazynu ? pobierzAtrybutyMagazynu([idMagazynu]) : Promise.resolve({ ok: true, data: {} }),
        // Bez karty zostaje pojemność nominalna i sprawność 90% z kalkulatora mocy.
        idMagazynu ? pobierzModel('magazyn', idMagazynu).catch(() => null) : Promise.resolve(null),
      ])
      if (anulowane) return
      setKartaMagazynu(karta ? magazynZKatalogu(karta) : null)
      // Nieudane pobranie zostawiamy jako PUSTĄ listę, nie jako błąd na wierzchu.
      // Brak zalet czyni dokument uboższym, ale nadal poprawnym — komunikat
      // o awarii w ofercie dla klienta byłby gorszy niż cicha nieobecność sekcji.
      setAtrybuty({
        falownik: f.ok ? (f.data[String(idFalownika)] || {}) : {},
        magazyn: m.ok ? (m.data[String(idMagazynu)] || {}) : {},
      })
    }

    wczytaj()
    return () => { anulowane = true }
  }, [otwarty, idFalownika, idMagazynu])

  /**
   * Produkcja i opłacalność liczone TU, raz, wspólnym silnikiem (`lib/ekonomiaKlienta.js`).
   *
   * Okładka pokazuje roczną produkcję, strona produkcji tę samą liczbę z autokonsumpcją, a strona
   * opłacalności zwrot. Gdyby każda liczyła po swojemu, klient dostałby na jednej ofercie dwie różne
   * prognozy dla tej samej instalacji. Ten sam silnik liczy Projekt 3D, więc te same dane dają tam
   * ten sam zwrot. Koszt: cena z karty klienta, potem cena oferty albo suma zestawu, potem widełki.
   */
  const zKosztami = Boolean(koszty)
  const ekonomiaWynik = useMemo(() => ((otwarty && dane?.zalecany && zestaw) ? ekonomiaKlienta(wejscieOferty({
    daneProjektu: projekt,
    zestaw: { kwp: zestaw.kwp, kosztFalownikaZl: zestaw.kosztFalownikaZl, pojemnoscMagazynuKwh: zestaw.pojemnoscMagazynuKwh },
    cenaKlientaZl: cenaKlienta?.brutto ?? null,
    kosztZestawuZl: zKosztami ? rachunek.razemZl : kosztZestawuZl,
    cenaOfertyZl: zKosztami ? null : cenaZewnetrznaZl,
    kartaMagazynu,
    energia: przyKliencie ? energia : null,
  })) : null),
  // Zestaw przychodzi nowym obiektem przy każdym renderze rodzica, więc zależymy od jego liczb.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [otwarty, Boolean(dane?.zalecany), zestaw?.kwp, zestaw?.kosztFalownikaZl, zestaw?.pojemnoscMagazynuKwh,
    projekt, cenaKlienta?.brutto, zKosztami, rachunek.razemZl, kosztZestawuZl, cenaZewnetrznaZl, kartaMagazynu, przyKliencie, energia])
  const prognozaWynik = ekonomiaWynik?.prognoza || null
  // Skąd produkcja na stronie dokumentu: „Projekt 3D, z uwzględnieniem cienia” albo „średnia dla województwa”.
  const zrodloProdukcji = jestPolska && ekonomiaWynik
    ? opisZrodlaProdukcji(ekonomiaWynik.zrodlaDanych, ekonomiaWynik.wojewodztwo)
    : ''
  const produkcjaZ3D = ekonomiaWynik?.zrodlaDanych?.produkcja?.startsWith('3d') || false

  if (!otwarty) return null

  // Okno otwiera się NATYCHMIAST po kliknięciu, jeszcze przed policzeniem — a gdy
  // brakuje danych katalogowych, w środku staje formularz. Wariant „licz najpierw,
  // pokaż potem" znaczył sekundę bez żadnej reakcji po kliknięciu, a przy braku
  // danych — komunikat pod przyciskiem zamiast okna, czyli ślepy zaułek.
  const gotowe = Boolean(dane?.zalecany)

  const z = dane?.zalecany
  const inv = dane?.inverter
  const data = new Date().toLocaleDateString(KRAJ.locale, {
    day: '2-digit', month: '2-digit', year: 'numeric',
  })

  // Warunki doboru na osiach: dozwolony zakres i wartość wypadkowa (patrz PasekWarunku).
  const warunki = gotowe ? warunkiDoboru(dane) : []

  // ── Dane wspólne dla wszystkich stron dokumentu ────────────────────────────

  const nazwaFirmy = profilKonta?.company?.name
  const kwpZestawu = zestaw?.kwp ?? z?.moc_kwp
  const podtytulStrony = [
    t('modal.podtytul_data', { data }),
    kwpZestawu ? `${String(kwpZestawu).replace('.', ',')} kWp` : null,
  ].filter(Boolean).join(' · ')

  // Opis instalacji (okładka, komponenty, zalety modułu) trzyma się liczby modułów z oferty;
  // tylko schemat łańcuchów pokazuje układ z doboru, z uwagą, gdy się różni.
  const ukladOpisu = gotowe && liczbaModulowOferty && zestaw?.kwp
    ? { ...z, modulow_razem: liczbaModulowOferty, moc_kwp: zestaw.kwp }
    : z

  // Zalety: falownik i magazyn z danych badawczych, moduły ze specyfikacji
  // katalogowej (sprawność, gwarancje, wykonanie ramy) i z doboru.
  const specModulu = zestaw?.items?.find((p) => p.category === 'panels')?.spec
  const zaletyWg = {
    panels: gotowe ? zaletyModulu({ panel: dane.panel, uklad: ukladOpisu, spec: specModulu }) : [],
    inverter: zaletyFalownika(atrybuty.falownik?.atrybuty),
    battery: zaletyMagazynu(atrybuty.magazyn?.atrybuty, atrybuty.magazyn?.sygnaly),
    // Nazwa marki idzie do nagłówka sekcji („DLACZEGO DEYE"), więc bierzemy ją
    // z tej samej odpowiedzi co zalety — nie zgadujemy jej z nazwy modelu.
    inverter_marka: atrybuty.falownik?.producent || '',
    battery_marka: atrybuty.magazyn?.producent || '',
  }

  // Strony liczone z tego, co dokument FAKTYCZNIE zawiera: bez kosztów nie ma strony ceny, poza Polską
  // nie ma opłacalności. Wcześniej stopka mówiła „z 5” także przy czterech stronach.
  const jestCena = Boolean((koszty && rachunek.razemZl) || cenaKlienta?.brutto)
  const jestInstalacja = Boolean(ujecie?.zGory || ujecie?.pola?.length)
  const strony = [
    ...(!trybOferty && zestaw ? ['okladka', ...(jestInstalacja ? ['instalacja'] : []), 'komponenty'] : []),
    'schemat',
    ...(zestaw ? ['produkcja'] : []),
    ...(zestaw && jestPolska ? ['oplacalnosc'] : []),
    ...(zestaw && jestCena ? ['cena'] : []),
  ]
  const STRON = strony.length
  const NR = Object.fromEntries(strony.map((k, i) => [k, i + 1]))

  // Spis treści z tych samych stron, które dokument naprawdę ma (bez okładki).
  const SPIS_TRESCI = ['komponenty', 'schemat', 'produkcja', 'oplacalnosc', 'cena'].filter((k) => strony.includes(k)).map((klucz) => ({
    tytul: t(`spis.${klucz}.tytul`),
    opis: t(`spis.${klucz}.opis`),
  }))


  /**
   * Sekcja niewidoczna na ekranie wraca NA WYDRUKU. Dokument PDF ma być kompletny
   * niezależnie od tego, którą zakładkę użytkownik miał akurat otwartą — inaczej
   * wydruk zależałby od przypadkowego stanu interfejsu.
   */
  const widoczna = (klucz) => (
    !zestaw || zakladka === klucz ? 'space-y-5' : 'hidden print:block'
  )

  // PORTAL DO <body>: reguła druku chowa wszystko poza `.vv-druk-korzen`, a ta
  // klasa musi być BEZPOŚREDNIM dzieckiem body — modal renderowany w środku karty
  // zestawu zostałby ukryty razem z nią.
  return createPortal(
    /* Kolor firmy wchodzi ZMIENNYMI CSS na korzeniu wydruku — stąd sięgają po
       niego wszystkie strony dokumentu. Zmienne, a nie klasy Tailwinda, bo kolor
       jest znany dopiero w czasie działania, a Tailwind buduje klasy przy
       kompilacji. Konto prywatne i gość dostają kolor platformy. */
    <div className="vv-druk-korzen fixed inset-0 z-50 flex items-center justify-center sm:p-6 print:static print:p-0"
         style={zmienneKoloru(wycena?.document_color)}>
      <div className="absolute inset-0 bg-gray-900/60 print:hidden" onClick={onZamknij} aria-hidden="true" />

      <div
        ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true"
        aria-label={t('modal.aria_okna')}
        className="relative bg-white w-full h-full sm:h-auto sm:max-w-4xl sm:max-h-[94vh] sm:rounded-2xl
                   shadow-2xl flex flex-col overflow-hidden print:shadow-none print:max-h-none print:max-w-none"
      >
        {/* Nagłówek okna NIE idzie na wydruk. Kontekst (logo firmy, nazwa, data)
            niesie teraz strona tytułowa, a ten pasek tylko dublował ją na górze
            pierwszej kartki i spychał okładkę na następną. */}
        <div className="flex items-start justify-between gap-3 px-5 sm:px-6 py-4 border-b border-gray-200 flex-shrink-0 print:hidden">
          <div className="flex items-center gap-3 min-w-0">
            <img src={`${process.env.PUBLIC_URL}/navLogo.png`} alt="VoltValue"
                 className="h-7 w-auto flex-shrink-0" />
            {/* Logo firmy OBOK naszego, oddzielone kreską: dokument idzie do
                klienta końcowego pod nazwą instalatora, ale liczby pochodzą od
                nas i nie udajemy, że jest inaczej. */}
            {wycena?.logo_data_uri && wycena.logo_data_uri !== LOGO_VOLTVALUE && (
              <>
                <span aria-hidden="true" className="h-6 w-px bg-gray-200 flex-shrink-0" />
                <img src={wycena.logo_data_uri} alt={t('modal.logo_firmy')}
                     className="h-7 w-auto max-w-[140px] object-contain flex-shrink-0" />
              </>
            )}
            <div className="min-w-0">
              <h2 className="text-base font-bold text-gray-900 leading-tight">
                {zestaw ? t('modal.tytul_projekt') : t('modal.tytul_schemat')}
                {oferta?.numer && <span className="ml-2 text-sm font-semibold text-gray-500">{oferta.numer}</span>}
              </h2>
              <p className="text-xs text-gray-500">
                {t('modal.wygenerowano', { data })}
              </p>
            </div>
          </div>
          <button type="button" onClick={onZamknij} aria-label={t('modal.zamknij')}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 print:hidden">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Zakładki — tylko gdy jest co pokazać poza schematem */}
        {gotowe && zestaw && (
          <div className="flex gap-1 px-5 sm:px-6 pt-3 border-b border-gray-200 flex-shrink-0 print:hidden">
            {zakladki.map((z) => (
              <button key={z.key} type="button" onClick={() => setZakladka(z.key)}
                      style={zakladka === z.key ? { borderColor: 'var(--vv-dok)', color: 'var(--vv-dok-ciemny)' } : undefined}
                      className={`px-3.5 py-2 text-sm font-semibold rounded-t-lg border-b-2 -mb-px
                        ${zakladka === z.key ? '' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
                {t(`modal.zakladki.${!jestPolska && z.labelPozaPolska ? z.labelPozaPolska : z.key}`)}
              </button>
            ))}
          </div>
        )}

        {/* Podgląd jak przeglądarka PDF: białe kartki A4 na szarym tle; na wydruku samo białe. */}
        <div className="flex-1 overflow-y-auto bg-gray-100 px-3 sm:px-8 py-5 space-y-5 print:bg-white print:px-0">
          {/* VoltValue Projekt i schemat połączeń — dla zalogowanych. Bramka
              obejmuje CAŁĄ treść okna, a nie sam przycisk PDF: sam schemat na
              ekranie jest tym samym dokumentem, tylko bez druku. Nagłówek
              i „Zamknij" zostają czynne, żeby dało się wyjść bez szukania. */}
          <BramkaTresci
            maxWysokosc="max-h-[55vh]"
            tytul={t('modal.bramka_tytul')}
            opis={t('modal.bramka_opis')}
          >
          <div className="space-y-5">
          {ladowanie && !gotowe && (
            <p className="text-sm text-gray-500 py-6 text-center">{t('modal.licze')}</p>
          )}
          {blad && !gotowe && (
            <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2.5">
              {blad}
            </p>
          )}
          {!gotowe && !ladowanie && formularz}

          {gotowe && !trybOferty && (
          <div className={widoczna('projekt')}>
            {/* Okładka i lista sprzętu — dwie pierwsze strony dokumentu.
                Tylko dla PEŁNEGO zestawu: sam schemat połączeń (bez zestawu)
                to notatka techniczna, a nie oferta, i okładka byłaby w niej
                zbędnym pierwszym arkuszem. */}
            {zestaw && (
              <StronaTytulowa
                nazwaFirmy={nazwaFirmy}
                logoFirmy={wycena?.logo_data_uri}
                kwp={liczbaModulowOferty ? zestaw.kwp : z.moc_kwp}
                data={data}
                naGruncie={projekt.konstrukcja === 'grunt'}
                zMagazynem={Boolean(zestaw?.magazyn)}
                produkcjaKwh={prognozaWynik?.rocznie?.produkcjaKwh}
                uzyskKwhNaKwp={prognozaWynik?.uzyskJednostkowy}
                pojemnoscMagazynuKwh={zestaw.pojemnoscMagazynuKwh}
                cenaZl={cenaKoncowaZl}
                cenaNettoZl={cenaNettoZl}
                klient={klient}
                oferta={oferta ? { numer: oferta.numer, wazneDo: dataPl(oferta.wazneDo) } : null}
                ujecie3d={ujecie?.glowne || null}
                ujecieTrwa={Boolean(ujecie?.trwa)}
                stawkaVat={koszty ? rachunek.stawkaVat : null}
                liczbaModulow={liczbaModulowOferty || z.modulow_razem}
                mocModuluW={dane.panel?.pmax_w}
                spisTresci={SPIS_TRESCI}
                numer={NR.okladka} zeStron={STRON} podtytul={podtytulStrony}
              />
            )}
            {jestInstalacja && (
              <StronaInstalacji ujecie={ujecie} nazwaFirmy={nazwaFirmy}
                                numer={NR.instalacja} zeStron={STRON} podtytul={podtytulStrony} />
            )}
            <StronaKomponentow
              pozycje={zestaw?.items}
              mocKwp={ukladOpisu.moc_kwp}
              zaletyWg={zaletyWg}
              kompatybilnoscPotwierdzona={kompatybilnosc?.status === 'compatible'}
              nazwaFirmy={nazwaFirmy}
              numer={NR.komponenty} zeStron={STRON} podtytul={podtytulStrony}
            />
          </div>
          )}

          {/* Bez zestawu nie ma okładki ani listy komponentów, więc to schemat
              otwiera dokument i to on nie może mieć łamania przed sobą. */}
          {gotowe && (
          <div className={widoczna('schemat')}>
          <StronaDokumentu
            numer={NR.schemat} zeStron={STRON} nazwaFirmy={nazwaFirmy}
            podtytul={podtytulStrony} pierwsza={!zestaw || trybOferty}
            zNaglowkiem={Boolean(zestaw)}
          >
          <NaglowekSekcji
            tytul={zestaw ? 'Jak działa Twoja instalacja' : t('schemat.tytul')}
            opis={zestaw ? 'Skąd bierze się prąd i dokąd płynie w ciągu roku.' : opisUkladu(t, z, inv.mppt_count)}
          />
          {/* Projekt z oferty instalatora: liczba modułów jest dana, a nasz dobór układa
              tylko łańcuchy równej długości. Gdy układ się różni, mówimy to wprost,
              zamiast po cichu rysować inną instalację niż ta z oferty. */}
          {liczbaModulowOferty && z.modulow_razem !== liczbaModulowOferty && (
            <div className="mb-4 flex items-start gap-3 rounded-xl border border-gray-200 px-4 py-3 text-sm leading-6">
              <Znak rodzaj="sprawdz" rozmiar="sm" className="mt-0.5" />
              <p className="text-gray-700">
                <span className="font-semibold text-gray-900">
                  {t('schemat.inny_uklad_tytul', { count: liczbaModulowOferty, wSchemacie: z.modulow_razem })}
                </span>{' '}
                {t('schemat.inny_uklad_opis', { count: liczbaModulowOferty })}
              </p>
            </div>
          )}

          {/* Schemat zostaje BIAŁY także w ciemnym motywie — to rysunek techniczny,
              który trafia prosto do PDF-a dla instalatora („Pobierz PDF" drukuje ten
              modal). Gdyby szedł za motywem, wydruk z włączonym ciemnym byłby
              ciemną plamą, a kolory w samym SVG i tak są dobrane pod biały arkusz. */}
          {/* Klient dostaje prosty diagram przepływu energii (decyzja właściciela 2026-10-11); rysunek łańcuchów
              zostaje w dokumencie bez zestawu, czyli w samym schemacie dla instalatora. */}
          <div className="vv-arkusz rounded-xl p-1">
            {zestaw ? (
              <DiagramEnergii liczbaModulow={ukladOpisu.modulow_razem} mocModuluW={dane.panel?.pmax_w} kwp={ukladOpisu.moc_kwp}
                              nazwaFalownika={czystaNazwa(inv.nazwa)} mocFalownikaKw={inv.ac_power_kw}
                              magazynKwh={zestaw.pojemnoscMagazynuKwh || 0} rocznie={prognozaWynik?.rocznie} />
            ) : (
              <SchematStringow uklad={z} mpptCount={inv.mppt_count} mocAcKw={inv.ac_power_kw}
                               nazwaFalownika={inv.nazwa} nazwaModulu={dane.panel?.nazwa}
                               magazyn={zestaw?.magazyn} kompatybilnosc={kompatybilnosc} />
            )}
          </div>

          {/* Warunki doboru na osiach: dowód, że napięcia i prądy łańcuchów mieszczą się w granicach falownika
              z zapasem, w mrozie i w upale (właściciel 2026-10-11: zostają jako dowód bezpieczeństwa). */}
          <div className="mt-6">
            {zestaw && <p className="mb-2 text-sm text-gray-600">{opisUkladu(t, z, inv.mppt_count)}</p>}
            <div className="flex items-baseline gap-3 flex-wrap mb-1">
              <p className="text-base font-bold text-gray-900">{zestaw ? 'Dobór sprawdzony obliczeniami' : t('schemat.warunki_doboru')}</p>
              <p className="text-xs text-gray-400">
                {t('schemat.temperatury', { mroz: pl(dane.warunki?.t_min_c), upal: pl(dane.warunki?.t_max_c) })}
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-10 divide-y divide-gray-100 sm:divide-y-0">
              {warunki.map((w) => <PasekWarunku key={w.klucz} {...w} />)}
            </div>
          </div>

          {/* Dane wejściowe — bez nich wydruk nie da się zweryfikować */}
          <div className="mt-5 border-t border-gray-100 pt-4">
            <p className="text-xs font-semibold text-gray-500">{t('schemat.dane_z_kart')}</p>
            <div className="mt-2 grid sm:grid-cols-2 gap-x-8 gap-y-3 text-xs">
              <div>
                <p className="text-gray-500">{t('schemat.modul')}</p>
                <p className="font-semibold text-gray-900">{czystaNazwa(dane.panel?.nazwa)}</p>
                <p className="mt-0.5 text-gray-500 tabular-nums">
                  Voc {pl(dane.panel?.voc_v)} V · Vmp {pl(dane.panel?.vmp_v)} V ·
                  Isc {pl(dane.panel?.isc_a)} A · Imp {pl(dane.panel?.imp_a)} A · {pl(dane.panel?.pmax_w)} Wp
                </p>
              </div>
              <div>
                <p className="text-gray-500">{t('schemat.falownik')}</p>
                <p className="font-semibold text-gray-900">{czystaNazwa(inv.nazwa)}</p>
                <p className="mt-0.5 text-gray-500 tabular-nums">
                  {t('schemat.dc_maks')} {pl(inv.dc_max_voltage_v)} V · MPPT {pl(inv.mppt_v_min)}–{pl(inv.mppt_v_max)} V
                  {' · '}{inv.mppt_count} × MPPT · {pl(inv.ac_power_kw)} kW AC
                </p>
              </div>
            </div>
          </div>

          </StronaDokumentu>
          </div>
          )}

          {gotowe && zestaw && (
            <>
              <div className={widoczna('produkcja')}>
                <StronaProdukcji
                  dane={projekt} onZmiana={setProjekt} onEdycja={onEdycja}
                  zrodloProdukcji={zrodloProdukcji} produkcjaZ3D={produkcjaZ3D}
                  kwp={zestaw.kwp} pojemnoscMagazynuKwh={zestaw.pojemnoscMagazynuKwh}
                  prognoza={prognozaWynik}
                  nazwaFirmy={nazwaFirmy}
                  numer={NR.produkcja} zeStron={STRON} podtytul={podtytulStrony}
                />
              </div>
              <div className={widoczna('oplacalnosc')}>
                {/* Opłacalność, a po niej cena końcowa (netto i brutto), która zamyka dokument:
                    to ostatnia rzecz, którą klient czyta. Obie strony we wspólnej ramce z numerem. */}
                {/* Dotacja PME2 i cała opłacalność są polskie: poza Polską projekt idzie bez nich
                    (decyzja właściciela 2026-10-08) i bez pustych miejsc po nich. */}
                {jestPolska && trybOferty && dotacjaOferty != null && (
                  <p className="rounded-xl border border-blue-100 bg-blue-50/60 px-3 py-2.5 text-xs leading-5 text-gray-600 print:hidden">
                    {dotacjaOferty > 0 ? t('modal.dotacja_pme2') : t('modal.dotacja_zero')}
                  </p>
                )}
                {jestPolska && (
                  <StronaDokumentu numer={NR.oplacalnosc} zeStron={STRON} nazwaFirmy={nazwaFirmy} podtytul={podtytulStrony}>
                    <NaglowekSekcji tytul={t('oplacalnosc.tytul')} opis={t('oplacalnosc.opis')} />
                    {/* Zwrot z tej samej kwoty, którą klient widzi na ostatniej stronie (silnik wyżej). */}
                    <SekcjaOplacalnosci dane={projekt} onZmiana={setProjekt} onEdycja={onEdycja} ekonomia={ekonomiaWynik} />
                  </StronaDokumentu>
                )}
                {jestCena && (
                  <StronaCeny
                    kwotaZl={cenaKoncowaZl}
                    nettoZl={cenaNettoZl}
                    stawkaVat={cenaKlienta ? null : rachunek.stawkaVat}
                    kwp={zestaw.kwp}
                    pojemnoscMagazynuKwh={zestaw.pojemnoscMagazynuKwh}
                    uslugiDodatkowe={cenaKlienta?.uslugi || []}
                    sygnalyMagazynu={atrybuty.magazyn?.sygnaly}
                    serwisFalownikaWPolsce={
                      (atrybuty.falownik?.atrybuty || [])
                        .some((a) => a.pole === 'serwis_pl_czy_jest' && /^tak/i.test(a.wartosc || ''))
                    }
                    nazwaFirmy={nazwaFirmy}
                    numer={NR.cena} zeStron={STRON} podtytul={podtytulStrony}
                  />
                )}
              </div>
            </>
          )}

          {/* Zastrzeżenie: krótko i szaro. Ściana tekstu w ramce i tak nie była
              czytana, a jej rozmiar sugerował, że dokument jest bardziej wiążący,
              niż jest naprawdę.

              NA WYDRUKU tylko wtedy, gdy nie ma zestawu. Przy pełnym dokumencie
              to samo zastrzeżenie — w pełniejszej wersji — zamyka stronę ceny,
              a dwa razy w jednym PDF-ie brzmiałoby jak asekuracja. */}
          <p className={`text-xs text-gray-400 leading-relaxed border-t border-gray-100 pt-4
                         ${zestaw ? 'print:hidden' : ''}`}>
            {t('modal.zastrzezenie')}
          </p>
          </div>
          </BramkaTresci>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-5 sm:px-6 py-3.5
                        border-t border-gray-200 bg-gray-50 flex-shrink-0 print:hidden">
          <p className="text-xs text-gray-400">{t('modal.stopka', { data })}</p>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onZamknij}
                    className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600
                               border border-gray-200 bg-white hover:bg-gray-50">
              {t('modal.zamknij')}
            </button>
            {/* Bez konta nie ma czego drukować: pod bramką stoi rozmyta treść,
                a wydruk oddałby ją dokładnie taką — rozmytą, z kartą zachęty
                w poprzek dokumentu. */}
            {gotowe && zalogowany && (
              <button type="button"
                      onClick={() => {
                        // PDF od konta Free (FE-05): bez konta okno rejestracji zamiast druku.
                        if (!isAuthenticated) { setOknoRejestracji(true); return }
                        zdarzenie('projekt_pdf'); aktywnosc('oferta_pdf'); window.print()
                      }}
                      style={{ backgroundColor: 'var(--vv-dok)', color: 'var(--vv-dok-na-pasku)' }}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg
                                 text-sm font-semibold hover:opacity-90">
                <ArrowDownTrayIcon className="w-4 h-4" />
                {t('modal.pobierz_pdf')}
              </button>
            )}
          </div>
        </div>
      </div>
      {oknoRejestracji && (
        <OknoRejestracji
          tytul="Załóż darmowe konto, żeby pobrać PDF"
          opis="Projekt i ofertę w PDF pobierzesz na darmowym koncie. Wystarczy e-mail i hasło."
          onZamknij={() => setOknoRejestracji(false)}
        />
      )}
    </div>,
    document.body,
  )
}

export default ModalSchematu
